import * as React from 'react'
import { RotateCcw } from 'lucide-react'
import { focalPointFromObjectPosition, objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'

/** The shapes this image will actually be shown at, so the preview is not abstract. */
const PREVIEWS: ReadonlyArray<{ label: string; ratio: string }> = [
  { label: 'Wide header', ratio: '3 / 1' },
  { label: 'Card', ratio: '4 / 3' },
  { label: 'Thumbnail', ratio: '4 / 3' },
]

/**
 * How long the arrow keys have to go quiet before the point is stored. Long
 * enough that a held key — which repeats around thirty times a second — is one
 * write rather than thirty, short enough that a single nudge lands at once.
 */
const KEY_COMMIT_DELAY_MS = 300

interface Props {
  url: string
  alt: string
  /** Null means nobody has chosen; the fallback is used and the crosshair sits there. */
  focalPoint: FocalPoint | null
  /** What an unfocused image uses, so "reset" returns to the real default. */
  fallback?: string
  /**
   * Store the point. Called when an adjustment settles — on releasing a drag,
   * not during it — because callers persist this, and a write per pointer event
   * is both a flood of requests and a race between them.
   */
  onChange: (focal: FocalPoint | null) => void
  disabled?: boolean
}

/** Part of an element's box, as fractions of it. */
interface BoxPart {
  left: number
  top: number
  width: number
  height: number
}

const WHOLE_BOX: BoxPart = { left: 0, top: 0, width: 1, height: 1 }

/**
 * Where the photograph is painted inside its element. `object-contain`
 * letterboxes one whose shape is not the element's: a tall photograph held to
 * the frame's height leaves a band either side, and a point read off the whole
 * element would not be the point on the picture. Before the image has loaded
 * there is nothing to letterbox, and the whole box is used.
 */
function paintedPart(img: HTMLImageElement, box: { width: number; height: number }): BoxPart {
  const { naturalWidth, naturalHeight } = img
  if (naturalWidth === 0 || naturalHeight === 0 || box.width === 0 || box.height === 0) return WHOLE_BOX
  const scale = Math.min(box.width / naturalWidth, box.height / naturalHeight)
  const width = (naturalWidth * scale) / box.width
  const height = (naturalHeight * scale) / box.height
  return { left: (1 - width) / 2, top: (1 - height) / 2, width, height }
}

/**
 * Read a pointer position as fractions of the painted photograph, clamped to it.
 * Null while the image has no size, which would otherwise divide by zero.
 */
function pointFrom(img: HTMLImageElement, clientX: number, clientY: number): FocalPoint | null {
  const box = img.getBoundingClientRect()
  if (box.width === 0 || box.height === 0) return null
  const part = paintedPart(img, box)
  const clamp = (n: number): number => Math.min(1, Math.max(0, n))
  const along = (offset: number, size: number, start: number, length: number): number =>
    Math.round(clamp((offset / size - start) / length) * 1000) / 1000
  return {
    x: along(clientX - box.left, box.width, part.left, part.width),
    y: along(clientY - box.top, box.height, part.top, part.height),
  }
}

/**
 * Point at the subject of a photograph, so every crop keeps it.
 *
 * One image is shown at several shapes here — a venue cover is 16:10 on a phone and
 * 3:1 on a desktop, a space photograph is 4:3 in its editor and a small thumbnail
 * in a list — and `object-fit: cover` crops to the middle, which cuts the top off a
 * room whose subject sits high in frame. Two numbers fix it with no image
 * processing: `object-position` does the work in the browser.
 *
 * The previews beside it are the part that makes this legible. A lone crosshair
 * does not tell anybody what they are fixing; the same photograph shown at the
 * shapes it will really appear in does, and it updates as the point moves.
 *
 * Keyboard: focus the image and use the arrow keys. This is a real requirement
 * rather than politeness — a click-only target on an image is unreachable
 * otherwise, and a focal point is exactly the kind of fine adjustment somebody
 * wants to nudge rather than aim.
 */
export function FocalPointPicker({
  url,
  alt,
  focalPoint,
  fallback = '50% 50%',
  onChange,
  disabled = false,
}: Props): React.ReactElement {
  const imageRef = React.useRef<HTMLImageElement>(null)
  const [dragging, setDragging] = React.useState(false)
  // Where the photograph sits in its frame, so the crosshair is drawn on the
  // picture and not on the letterbox. Measured when it loads and when it resizes.
  const [painted, setPainted] = React.useState<BoxPart>(WHOLE_BOX)
  // Where the pointer is, before it is anywhere worth storing: the crosshair and
  // the previews follow this so they keep up with the hand, while `focalPoint`
  // only catches up once a committed point has been round-tripped. A draft
  // carries the photograph it was aimed at, so a new image is shown at its own
  // stored point rather than at the last one's aim, with no reset to remember.
  const [draft, setDraft] = React.useState<{ url: string; point: FocalPoint | null } | null>(null)
  const pending = React.useRef<FocalPoint | null>(null)
  const commitTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChangeRef = React.useRef(onChange)
  React.useEffect(() => {
    onChangeRef.current = onChange
  })

  // `undefined` is "nothing drafted, show what is stored". `null` is a reset
  // somebody has just asked for — a different thing, and one that has to survive
  // until the stored point catches up.
  const drafted = draft && draft.url === url ? draft.point : undefined
  const current = drafted === undefined ? focalPoint : drafted
  const position = objectPositionOf(current, fallback)
  // Where to draw the crosshair when nothing is chosen: on the fallback, so the
  // marker sits where the crop is actually centered rather than lying about it.
  const shown = current ?? focalPointFromObjectPosition(fallback)

  const measure = React.useCallback((): void => {
    const img = imageRef.current
    if (img) setPainted(paintedPart(img, img.getBoundingClientRect()))
  }, [])

  React.useEffect(() => {
    const img = imageRef.current
    if (!img || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(img)
    return () => observer.disconnect()
  }, [measure])

  // A nudge still sitting in the timer when this unmounts is one somebody made
  // and watched happen, so it is sent rather than dropped.
  React.useEffect(
    () => () => {
      if (!commitTimer.current) return
      clearTimeout(commitTimer.current)
      const point = pending.current
      pending.current = null
      if (point) onChangeRef.current(point)
    },
    [],
  )

  function clearPending(): void {
    if (commitTimer.current) clearTimeout(commitTimer.current)
    commitTimer.current = null
    pending.current = null
  }

  function commit(point: FocalPoint | null): void {
    clearPending()
    setDraft({ url, point })
    onChange(point)
  }

  /** Aim now, store when the hand stops: one write per adjustment, not per frame. */
  function commitWhenSettled(point: FocalPoint): void {
    pending.current = point
    if (commitTimer.current) clearTimeout(commitTimer.current)
    commitTimer.current = setTimeout(() => {
      commitTimer.current = null
      const settled = pending.current
      pending.current = null
      if (settled) onChangeRef.current(settled)
    }, KEY_COMMIT_DELAY_MS)
  }

  /** Follow the pointer. Nothing is stored until the drag is released. */
  function aim(clientX: number, clientY: number): void {
    if (disabled || !imageRef.current) return
    const point = pointFrom(imageRef.current, clientX, clientY)
    if (point === null) return
    clearPending()
    setDraft({ url, point })
  }

  function nudge(dx: number, dy: number): void {
    if (disabled) return
    const step = 0.02
    const next = {
      x: Math.min(1, Math.max(0, Number((shown.x + dx * step).toFixed(3)))),
      y: Math.min(1, Math.max(0, Number((shown.y + dy * step).toFixed(3)))),
    }
    setDraft({ url, point: next })
    commitWhenSettled(next)
  }

  return (
    <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="space-y-2">
        <div
          role="application"
          aria-label={`Focal point for ${alt || 'this photograph'} — click, drag, or use the arrow keys`}
          tabIndex={disabled ? -1 : 0}
          onPointerDown={(e) => {
            if (disabled) return
            e.currentTarget.setPointerCapture(e.pointerId)
            setDragging(true)
            aim(e.clientX, e.clientY)
          }}
          onPointerMove={(e) => {
            if (dragging) aim(e.clientX, e.clientY)
          }}
          onPointerUp={(e) => {
            e.currentTarget.releasePointerCapture(e.pointerId)
            setDragging(false)
            // Where the hand came to rest is the point worth keeping — one
            // write for the whole drag, and no half-finished aim stored on the
            // way there.
            if (drafted) commit(drafted)
          }}
          onPointerCancel={(e) => {
            e.currentTarget.releasePointerCapture(e.pointerId)
            setDragging(false)
            if (drafted) commit(drafted)
          }}
          onKeyDown={(e) => {
            const move = arrowNudge(e.key)
            if (move === null) return
            e.preventDefault()
            nudge(move.dx, move.dy)
          }}
          className="relative overflow-hidden rounded-md border border-line bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          style={{ cursor: disabled ? 'default' : 'crosshair' }}
        >
          <img
            ref={imageRef}
            src={url}
            alt={alt}
            className="block max-h-72 w-full object-contain"
            draggable={false}
            onLoad={measure}
          />
          {/* Drawn over the painted photograph, since the point is in image space. */}
          <span
            aria-hidden="true"
            // A ring rather than a hand-rolled shadow: it is a 1px outline for
            // contrast against an unknown photograph, not a floating layer.
            className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-round border-2 border-white ring-1 ring-black/50"
            style={{
              left: `${String((painted.left + shown.x * painted.width) * 100)}%`,
              top: `${String((painted.top + shown.y * painted.height) * 100)}%`,
            }}
          />
        </div>
        {!disabled ? (
          <div className="flex items-center gap-2">
            <p className="text-xs text-ink-muted">
              Click the subject, or focus the image and use the arrow keys.
            </p>
            {current ? (
              <button
                type="button"
                onClick={() => commit(null)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-ink-muted transition-colors hover:text-ink"
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="space-y-2">
        {PREVIEWS.map((preview, index) => (
          <li key={preview.label} className="space-y-0.5">
            <div
              className="overflow-hidden rounded border border-line bg-surface-sunken"
              style={{ aspectRatio: preview.ratio, width: index === 2 ? '4.5rem' : '100%' }}
            >
              <img
                src={url}
                alt=""
                className="h-full w-full object-cover"
                style={{ objectPosition: position }}
              />
            </div>
            <p className="text-tiny uppercase tracking-wide text-ink-muted">
              {preview.label}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Arrow keys move the crosshair. Any other key is not a nudge. */
function arrowNudge(key: string): { dx: number; dy: number } | null {
  switch (key) {
    case 'ArrowLeft':
      return { dx: -1, dy: 0 }
    case 'ArrowRight':
      return { dx: 1, dy: 0 }
    case 'ArrowUp':
      return { dx: 0, dy: -1 }
    case 'ArrowDown':
      return { dx: 0, dy: 1 }
    default:
      return null
  }
}
